import { Box, Card, CardContent, GridLegacy as Grid, Typography, useTheme } from "@mui/material";
import BasicMap from "components/BasicMap";
import { CountyPicker } from "components/CountyPicker";
import { FormTextField } from "components/Forms/FormTextField";
import { MunicipalityPicker } from "components/MunicipalityPicker";
import { StatePicker } from "components/StatePicker";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { useEffect } from "react";
import { Controller } from "react-hook-form";

import { CountryPicker } from "./CountryPicker";

const showMunicipalityStates = ["PA", "WI"];
export const PhysicalAddress = ({
  showMap,
  readOnly,
}: {
  showMap?: boolean;
  readOnly?: boolean;
}) => {
  const theme = useTheme();
  const { control, metadata, setValue, watch } = useValidatingFormContext();
  const lat = watch("latitude");
  const long = watch("longitude");
  const state = watch("state") ?? "";
  const county = watch("county");
  const inStateWithMunicipality = state && showMunicipalityStates.includes(state);

  useEffect(() => {
    if (state && !showMunicipalityStates.includes(state)) {
      setValue("municipality", null);
    }
  }, [state, setValue]);

  return (
    <Grid
      container
      columnSpacing={theme.spacing(3)}
      rowSpacing={theme.spacing(metadata?.isDisplayOnly ? 3 : 1)}
      marginTop={theme.spacing(metadata?.isDisplayOnly ? 0 : 2)}
    >
      <Grid item xs={12} lg={4}>
        <FormTextField
          name="streetAddress1"
          label="Street Address"
          control={control}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormTextField
          name="streetAddress2"
          label="Street Address 2"
          control={control}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={0} lg={4} />

      <Grid item xs={12} lg={4}>
        <FormTextField name="city" label="City" control={control} disabled={readOnly} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <StatePicker
          name="state"
          countryFieldName="country"
          control={control}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormTextField name="zip" label="Zip" control={control} disabled={readOnly} />
      </Grid>

      <Grid item xs={12} lg={4}>
        <Controller
          name="county"
          control={control}
          render={(props) => <CountyPicker {...props} facilityState={state} disabled={readOnly} />}
        />
      </Grid>
      {inStateWithMunicipality && (
        <Grid item xs={12} lg={4}>
          <Controller
            name="municipality"
            control={control}
            render={({ field, fieldState }) => (
              <MunicipalityPicker
                {...field}
                {...fieldState}
                disabled={readOnly}
                facilityState={state}
                facilityCounty={county}
              />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} lg={4}>
        <CountryPicker name="country" control={control} disabled={readOnly} />
      </Grid>
      {!inStateWithMunicipality && <Grid item xs={0} lg={4} />}

      <Grid item xs={12} lg={4}>
        <FormTextField name="latitude" label="Latitude" control={control} disabled={readOnly} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormTextField name="longitude" label="Longitude" control={control} disabled={readOnly} />
      </Grid>
      <Grid item xs={0} lg={4} />

      {showMap && (
        <Grid item xs={12}>
          <Box width="full" height="269px">
            {!Number.isNaN(parseFloat(lat)) && !Number.isNaN(parseFloat(long)) && (
              <BasicMap latitude={Number(lat)} longitude={Number(long)} />
            )}
            {(Number.isNaN(parseFloat(lat)) || Number.isNaN(parseFloat(long))) && (
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Map will display here once latitude and longitude are entered.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
